import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import { collection, addDoc, getDocs, query, orderBy, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../firebase";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
	IconUpload,
	IconLoader2,
	IconTrash,
	IconEye,
	IconChevronDown,
	IconMail,
	IconMailForward,
} from "@tabler/icons-react";
import EmailModal from "../../components/EmailModal";

type ExcelData = {
	sheetName: string;
	data: any[];
};

type CustomerData = {
	id: string;
	fileName: string;
	uploadedAt: string;
	sheets: {
		name: string;
		data: any[];
		rowCount: number;
	}[];
};

const hasEmailField = (row: any): boolean => {
	const rowEntries = Object.entries(row);
	return rowEntries.some(([key, value]) => {
		const lowerKey = key.toLowerCase();
		const lowerValue = String(value).toLowerCase();
		return (
			(lowerKey.includes("email") || lowerKey === "e-mail") && lowerValue.includes("@") && lowerValue.includes(".")
		);
	});
};

const getEmailFromRow = (row: any): string | null => {
	const rowEntries = Object.entries(row);
	const emailEntry = rowEntries.find(([key, value]) => {
		const lowerKey = key.toLowerCase();
		const lowerValue = String(value).toLowerCase();
		return (
			(lowerKey.includes("email") || lowerKey === "e-mail") && lowerValue.includes("@") && lowerValue.includes(".")
		);
	});
	return emailEntry ? String(emailEntry[1]) : null;
};

// Memoized row component
const TableRow = memo(
	({
		row,
		rowIndex,
		fileId,
		sheetName,
		isSelected,
		onRowSelect,
		onEmailClick,
	}: {
		row: any;
		rowIndex: number;
		fileId: string;
		sheetName: string;
		isSelected: boolean;
		onRowSelect: (fileId: string, sheetName: string, rowIndex: number) => void;
		onEmailClick: (email: string) => void;
	}) => {
		const hasEmail = hasEmailField(row);
		const email = hasEmail ? getEmailFromRow(row) : null;

		const handleCheckboxChange = useCallback(() => {
			onRowSelect(fileId, sheetName, rowIndex);
		}, [fileId, sheetName, rowIndex, onRowSelect]);

		const handleEmailButtonClick = useCallback(() => {
			if (email) {
				onEmailClick(email);
			}
		}, [email, onEmailClick]);

		return (
			<tr className={`${isSelected ? "bg-primary/5" : ""} hover:bg-gray-50 dark:hover:bg-gray-800/50`}>
				<td className='whitespace-nowrap px-3 py-4 text-sm'>
					<input
						type='checkbox'
						checked={isSelected}
						disabled={!hasEmail || !email}
						onChange={handleCheckboxChange}
						className='h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed'
					/>
				</td>
				{Object.values(row).map((cell: any, cellIndex) => (
					<td
						key={cellIndex}
						className={`whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400 ${
							row.isEmailSent ? "italic opacity-75" : ""
						}`}>
						{cell?.toString()}
						{cellIndex === 0 && row.isEmailSent && (
							<span className='ml-2 inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20'>
								Email Sent
							</span>
						)}
					</td>
				))}
				<td className='relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6'>
					{hasEmail && (
						<button onClick={handleEmailButtonClick} className='text-primary hover:text-primary/80'>
							<IconMail className='h-5 w-5' />
							<span className='sr-only'>Send Email</span>
						</button>
					)}
				</td>
			</tr>
		);
	},
);

// Memoized sheet component
const SheetComponent = memo(
	({
		file,
		sheet,
		isExpanded,
		selectedRows,
		onRowSelect,
		onEmailClick,
		renderCheckbox,
	}: {
		file: CustomerData;
		sheet: any;
		isExpanded: boolean;
		selectedRows: { [key: string]: Set<number> };
		onRowSelect: (fileId: string, sheetName: string, rowIndex: number) => void;
		onEmailClick: (email: string) => void;
		renderCheckbox: (file: CustomerData, sheet: any) => React.ReactElement;
	}) => {
		if (!isExpanded) return null;

		return (
			<div className='border-t border-gray-200 dark:border-gray-700 p-4 overflow-x-auto'>
				<table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
					<thead>
						<tr>
							<th className='px-3 py-3.5 text-left text-sm font-medium text-gray-900 dark:text-gray-200'>
								{renderCheckbox(file, sheet)}
							</th>
							{Object.keys(sheet.data[0] || {}).map((header) => (
								<th key={header} className='px-3 py-3.5 text-left text-sm font-medium text-gray-900 dark:text-gray-200'>
									{header}
								</th>
							))}
							<th className='relative py-3.5 pl-3 pr-4 sm:pr-6'>
								<span className='sr-only'>Actions</span>
							</th>
						</tr>
					</thead>
					<tbody className='divide-y divide-gray-200 dark:divide-gray-700'>
						{sheet.data.map((row: any, rowIndex: number) => (
							<TableRow
								key={rowIndex}
								row={row}
								rowIndex={rowIndex}
								fileId={file.id}
								sheetName={sheet.name}
								isSelected={selectedRows[`${file.id}-${sheet.name}`]?.has(rowIndex) || false}
								onRowSelect={onRowSelect}
								onEmailClick={onEmailClick}
							/>
						))}
					</tbody>
				</table>
			</div>
		);
	},
);

export default function CustomerDataPage() {
	const [uploading, setUploading] = useState(false);
	const [loading, setLoading] = useState(true);
	const [customerData, setCustomerData] = useState<CustomerData[]>([]);
	const [expandedFile, setExpandedFile] = useState<string | null>(null);
	const [expandedSheet, setExpandedSheet] = useState<string | null>(null);
	const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
	const [selectedEmail, setSelectedEmail] = useState("");
	const [selectedRows, setSelectedRows] = useState<{ [key: string]: Set<number> }>({});
	const fileInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		fetchCustomerData();
	}, []);

	const fetchCustomerData = async () => {
		try {
			const customerDataQuery = query(collection(firestore, "customerData"), orderBy("uploadedAt", "desc"));
			const querySnapshot = await getDocs(customerDataQuery);
			const data = querySnapshot.docs.map((doc) => {
				const fileData = doc.data();
				// Ensure isEmailSent field exists in all rows
				const updatedSheets = fileData.sheets.map((sheet: any) => ({
					...sheet,
					data: sheet.data.map((row: any) => ({
						...row,
						isEmailSent: row.isEmailSent || false,
					})),
				}));

				return {
					id: doc.id,
					...fileData,
					sheets: updatedSheets,
				};
			}) as CustomerData[];
			setCustomerData(data);
		} catch (error) {
			console.error("Error fetching customer data:", error);
			toast.error("Failed to load customer data");
		} finally {
			setLoading(false);
		}
	};

	const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		try {
			setUploading(true);

			// Read the Excel file
			const data = await file.arrayBuffer();
			const workbook = XLSX.read(data);

			// Process each worksheet
			const processedData: ExcelData[] = workbook.SheetNames.map((sheetName) => {
				const worksheet = workbook.Sheets[sheetName];

				// Convert to array of arrays first to preserve structure
				const arrayData = XLSX.utils.sheet_to_json(worksheet, {
					header: 1,
					defval: "", // Use empty string for empty cells
					raw: false, // Convert everything to string
				});

				// Get headers from first row
				const headers = arrayData[0] as string[];

				// Convert array data to objects while preserving column order
				const jsonData = arrayData.slice(1).map((row) => {
					const rowData: { [key: string]: any } = {};
					headers.forEach((header, index) => {
						rowData[header] = (row as any[])[index] || ""; // Handle empty cells
					});
					// Add isEmailSent field to each row
					rowData.isEmailSent = false;
					return rowData;
				});

				return {
					sheetName,
					data: jsonData,
				};
			});

			// Save to Firestore
			const timestamp = new Date().toISOString();
			const customerDataRef = collection(firestore, "customerData");

			await addDoc(customerDataRef, {
				fileName: file.name,
				uploadedAt: timestamp,
				sheets: processedData.map((sheet) => ({
					name: sheet.sheetName,
					data: sheet.data,
					rowCount: sheet.data.length,
				})),
			});

			toast.success("Excel file uploaded successfully");
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
			fetchCustomerData();
		} catch (error) {
			console.error("Error uploading file:", error);
			toast.error("Failed to upload Excel file");
		} finally {
			setUploading(false);
		}
	};

	const handleDelete = async (id: string) => {
		try {
			await deleteDoc(doc(firestore, "customerData", id));
			toast.success("File deleted successfully");
			fetchCustomerData();
		} catch (error) {
			console.error("Error deleting file:", error);
			toast.error("Failed to delete file");
		}
	};

	const handleRowSelection = useCallback((fileId: string, sheetName: string, rowIndex: number) => {
		const key = `${fileId}-${sheetName}`;
		setSelectedRows((prev) => {
			const newSelectedRows = { ...prev };
			const currentSet = new Set(prev[key] || []);

			if (currentSet.has(rowIndex)) {
				currentSet.delete(rowIndex);
			} else {
				currentSet.add(rowIndex);
			}

			newSelectedRows[key] = currentSet;
			return newSelectedRows;
		});
	}, []);

	const getValidEmailIndices = useCallback((data: any[]) => {
		return data
			.map((row, index) => (hasEmailField(row) && getEmailFromRow(row) ? index : null))
			.filter((index): index is number => index !== null);
	}, []);

	const handleSelectAll = useCallback(
		(fileId: string, sheetName: string, data: any[]) => {
			const key = `${fileId}-${sheetName}`;
			const validEmailIndices = getValidEmailIndices(data);

			setSelectedRows((prev) => {
				const currentSet = new Set(prev[key] || []);
				const allSelected = validEmailIndices.every((index) => currentSet.has(index));

				return {
					...prev,
					[key]: allSelected ? new Set() : new Set(validEmailIndices),
				};
			});
		},
		[getValidEmailIndices],
	);

	const isHeaderChecked = useCallback(
		(fileId: string, sheetName: string, data: any[]) => {
			const key = `${fileId}-${sheetName}`;
			const validEmailIndices = getValidEmailIndices(data);
			const currentSet = selectedRows[key] || new Set();

			return validEmailIndices.length > 0 && validEmailIndices.every((index) => currentSet.has(index));
		},
		[selectedRows, getValidEmailIndices],
	);

	const renderCheckbox = useCallback(
		(file: CustomerData, sheet: any) => (
			<input
				type='checkbox'
				onChange={() => handleSelectAll(file.id, sheet.name, sheet.data)}
				checked={isHeaderChecked(file.id, sheet.name, sheet.data)}
				className='h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary'
			/>
		),
		[handleSelectAll, isHeaderChecked],
	);

	const getSelectedEmails = (fileId: string, sheetName: string, data: any[]): string[] => {
		const key = `${fileId}-${sheetName}`;
		const selectedIndices = selectedRows[key] || new Set();
		return Array.from(selectedIndices)
			.map((index) => {
				const row = data[index];
				return getEmailFromRow(row);
			})
			.filter((email): email is string => email !== null);
	};

	const updateEmailSentStatus = async (fileId: string, sheetName: string, rowIndices: number[]) => {
		try {
			const fileRef = doc(firestore, "customerData", fileId);
			const fileDoc = customerData.find((f) => f.id === fileId);

			if (!fileDoc) return;

			const updatedSheets = fileDoc.sheets.map((sheet) => {
				if (sheet.name === sheetName) {
					const updatedData = sheet.data.map((row, index) => {
						if (rowIndices.includes(index)) {
							return { ...row, isEmailSent: true };
						}
						return row;
					});
					return { ...sheet, data: updatedData };
				}
				return sheet;
			});

			await updateDoc(fileRef, {
				sheets: updatedSheets,
			});

			// Update local state
			setCustomerData((prev) =>
				prev.map((file) => {
					if (file.id === fileId) {
						return { ...file, sheets: updatedSheets };
					}
					return file;
				}),
			);

			toast.success("Email sent status updated");
		} catch (error) {
			console.error("Error updating email sent status:", error);
			toast.error("Failed to update email sent status");
		}
	};

	const handleBatchEmail = (fileId: string, sheetName: string, data: any[]) => {
		const emails = getSelectedEmails(fileId, sheetName, data);
		if (emails.length === 0) {
			return;
		}
		setSelectedEmail(""); // Clear single email selection
		setIsEmailModalOpen(true);

		// Store the current selection for updating after sending
		const key = `${fileId}-${sheetName}`;
		const selectedIndices = Array.from(selectedRows[key] || new Set());

		// Pass the update function to EmailModal
		const handleEmailSent = async () => {
			await updateEmailSentStatus(fileId, sheetName, selectedIndices);
		};

		return handleEmailSent;
	};

	const ensureEmailSentField = async (fileId: string, fileData: any) => {
		try {
			const fileRef = doc(firestore, "customerData", fileId);
			const updatedSheets = fileData.sheets.map((sheet: any) => ({
				...sheet,
				data: sheet.data.map((row: any) => ({
					...row,
					isEmailSent: row.isEmailSent || false,
				})),
			}));

			await updateDoc(fileRef, {
				sheets: updatedSheets,
			});
		} catch (error) {
			console.error("Error updating file with isEmailSent field:", error);
		}
	};

	// Add another useEffect to update existing data
	useEffect(() => {
		if (!loading && customerData.length > 0) {
			customerData.forEach((file) => {
				const needsUpdate = file.sheets.some((sheet) => sheet.data.some((row) => row.isEmailSent === undefined));
				if (needsUpdate) {
					ensureEmailSentField(file.id, file);
				}
			});
		}
	}, [loading, customerData]);

	const handleEmailClick = useCallback((email: string) => {
		setSelectedEmail(email);
		setIsEmailModalOpen(true);
	}, []);

	if (loading) {
		return (
			<div className='flex h-[calc(100vh-4rem)] items-center justify-center'>
				<div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent'></div>
			</div>
		);
	}

	return (
		<div>
			<div className='mb-8'>
				<h1 className='text-2xl font-semibold text-gray-900 dark:text-white'>Customer Data</h1>
				<p className='mt-2 text-sm text-gray-700 dark:text-gray-400'>
					Upload and manage customer data from Excel files
				</p>
			</div>

			{/* Upload Section */}
			<div className='rounded-lg bg-white dark:bg-gray-800 p-6 shadow-sm ring-1 ring-gray-900/5 dark:ring-white/10'>
				<div className='flex items-center justify-between'>
					<div>
						<h3 className='text-base font-semibold text-gray-900 dark:text-white'>Upload Excel File</h3>
						<p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
							Upload your Excel file containing customer data. All worksheets will be processed.
						</p>
					</div>
					<div className='relative'>
						<input
							type='file'
							ref={fileInputRef}
							onChange={handleFileUpload}
							accept='.xlsx,.xls'
							className='hidden'
							disabled={uploading}
						/>
						<button
							onClick={() => fileInputRef.current?.click()}
							disabled={uploading}
							className='inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-black shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50 disabled:cursor-not-allowed'>
							{uploading ? (
								<>
									<IconLoader2 className='mr-2 h-4 w-4 animate-spin' />
									Uploading...
								</>
							) : (
								<>
									<IconUpload className='mr-2 h-4 w-4' />
									Upload Excel
								</>
							)}
						</button>
					</div>
				</div>
			</div>

			{/* Files List */}
			<div className='mt-8 space-y-4'>
				{customerData.map((file) => (
					<div
						key={file.id}
						className='rounded-lg bg-white dark:bg-gray-800 shadow-sm ring-1 ring-gray-900/5 dark:ring-white/10 overflow-hidden'>
						<div className='p-6'>
							<div className='flex items-center justify-between'>
								<div>
									<h3 className='text-base font-semibold text-gray-900 dark:text-white'>{file.fileName}</h3>
									<p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
										Uploaded on {new Date(file.uploadedAt).toLocaleString()}
									</p>
								</div>
								<div className='flex items-center space-x-4'>
									{file.sheets.map((sheet) => {
										const selectedCount = selectedRows[`${file.id}-${sheet.name}`]?.size || 0;
										if (selectedCount > 0) {
											return (
												<button
													key={sheet.name}
													onClick={() => handleBatchEmail(file.id, sheet.name, sheet.data)}
													className='inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-black shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'>
													<IconMailForward className='mr-2 h-4 w-4' />
													Send Email ({selectedCount} selected)
												</button>
											);
										}
										return null;
									})}
									<button
										onClick={() => setExpandedFile(expandedFile === file.id ? null : file.id)}
										className='text-gray-400 hover:text-gray-500 dark:hover:text-gray-300'>
										<IconEye className='h-5 w-5' />
									</button>
									<button
										onClick={() => handleDelete(file.id)}
										className='text-red-400 hover:text-red-500 dark:hover:text-red-300'>
										<IconTrash className='h-5 w-5' />
									</button>
								</div>
							</div>

							{/* Sheets List */}
							{expandedFile === file.id && (
								<div className='mt-6 space-y-4'>
									{file.sheets.map((sheet) => (
										<div key={sheet.name} className='rounded-lg border border-gray-200 dark:border-gray-700'>
											<button
												onClick={() =>
													setExpandedSheet(
														expandedSheet === `${file.id}-${sheet.name}` ? null : `${file.id}-${sheet.name}`,
													)
												}
												className='flex w-full items-center justify-between px-4 py-3 text-left'>
												<div>
													<span className='text-sm font-medium text-gray-900 dark:text-white'>{sheet.name}</span>
													<span className='ml-2 text-sm text-gray-500 dark:text-gray-400'>({sheet.rowCount} rows)</span>
												</div>
												<IconChevronDown
													className={`h-5 w-5 text-gray-400 transition-transform ${
														expandedSheet === `${file.id}-${sheet.name}` ? "rotate-180" : ""
													}`}
												/>
											</button>

											{/* Sheet Data */}
											{expandedSheet === `${file.id}-${sheet.name}` && (
												<SheetComponent
													file={file}
													sheet={sheet}
													isExpanded={true}
													selectedRows={selectedRows}
													onRowSelect={handleRowSelection}
													onEmailClick={handleEmailClick}
													renderCheckbox={renderCheckbox}
												/>
											)}
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				))}
			</div>

			<EmailModal
				isOpen={isEmailModalOpen}
				onClose={() => setIsEmailModalOpen(false)}
				recipientEmail={selectedEmail}
				onSend={async () => Promise.resolve()}
				onEmailSent={
					selectedEmail
						? async () => {
								const currentSheet = expandedSheet?.split("-")?.[1];
								const currentFile = customerData.find((f) => f.id === expandedFile);
								if (currentFile && currentSheet) {
									const rowIndex = currentFile.sheets
										.find((s) => s.name === currentSheet)
										?.data.findIndex((row) => getEmailFromRow(row) === selectedEmail);
									if (rowIndex !== undefined && rowIndex !== -1) {
										await updateEmailSentStatus(expandedFile!, currentSheet, [rowIndex]);
									}
								}
							}
						: undefined
				}
			/>
		</div>
	);
}
